import React from 'react';
import { Popover, StatusIndicator } from '@amzn/awsui-components-react/polaris';
import FormattingService from 'src/services/utils/FormattingService';
import { ColumnDef, ColumnPullStatus, PullStatusResponse } from 'src/models/segment-cdt/SegmentCdtTypes';

/** Wire status -> the indicator type and the word shown beside it. */
const PRESENTATION: Record<string, { type: 'success' | 'error' | 'in-progress' | 'pending', label: string }> = {
    [ColumnPullStatus.COMPLETED]: { type: 'success', label: 'Completed' },
    [ColumnPullStatus.FAILED]: { type: 'error', label: 'Failed' },
    [ColumnPullStatus.IN_PROGRESS]: { type: 'in-progress', label: 'In progress' },
    [ColumnPullStatus.PENDING]: { type: 'pending', label: 'Queued' },
};

// Reused rather than re-formatted here: this is the package's timestamp formatter (YYYY-MM-DD HH:mm), and
// `completedAt` is epoch milliseconds, which is what moment takes for a bare number. Module-scope instance,
// matching CalculationStepsService's use of it.
const formattingService = new FormattingService();

/**
 * When the pull finished, shown only for a terminal status.
 *
 * `completedAt` is null until terminal, so there is nothing to show while a pull runs. On the rare terminal
 * status with no `completedAt`, nothing is shown rather than falling back to `startedAt`: labelling a start
 * time as a finish time would be worse than omitting it.
 */
function completionTime(pullStatus: PullStatusResponse): string {
    const terminal = pullStatus.overallStatus === ColumnPullStatus.COMPLETED
        || pullStatus.overallStatus === ColumnPullStatus.FAILED;
    if (!terminal || pullStatus.completedAt == null) {
        return '';
    }
    return formattingService.formatTimestamp(pullStatus.completedAt);
}

/** A column's display name, falling back to its id (which is what the status map is keyed by). */
function columnLabel(columnId: string, columns: ColumnDef[]): string {
    const match = (columns ?? []).find((c) => c.columnId === columnId);
    return match?.displayName ?? columnId;
}

/**
 * The tracked GL pull's status, for the detail view's metadata row.
 *
 * <p>Reports the overall status as a word, and hangs the per-column detail off a popover. That per-column
 * map is the point: `configStatuses` and `errors` are maintained per column by the pull workflow, and the
 * failure banner already tells the user to "check the column pull statuses" - until now there was nowhere
 * to check them.
 *
 * <p>Renders nothing when no pull is being tracked. Unlike a reconciliation figure, absence here is not a
 * claim about anything: a table simply may not have had a pull this session, and a permanent "no pull"
 * label beside every idle table would be noise.
 */
export default function SegmentPullStatusIndicator(props: {
    pullStatus?: PullStatusResponse;
    columns: ColumnDef[];
}) {
    const { pullStatus, columns } = props;
    // No pull tracked, or one whose overall status the server did not report. The mapper omits the status
    // rather than defaulting it when the record carries none, so there is genuinely nothing to state.
    if (!pullStatus?.overallStatus) {
        return null;
    }

    const overall = PRESENTATION[pullStatus.overallStatus];
    if (!overall) {
        // A status outside the declared vocabulary: report it verbatim rather than guessing a category,
        // so a contract addition shows up as itself instead of being silently recategorised.
        return (
            <span>
                <strong>GL Pull:</strong>{' '}
                <StatusIndicator type="info">{String(pullStatus.overallStatus)}</StatusIndicator>
            </span>
        );
    }

    const configStatuses = pullStatus.configStatuses ?? {};
    const errors = pullStatus.errors ?? {};
    const columnIds = Object.keys(configStatuses);

    const indicator = <StatusIndicator type={overall.type}>{overall.label}</StatusIndicator>;
    const finishedAt = completionTime(pullStatus);
    const timestamp = finishedAt
        ? <span className="segmentPullStatusTime">{finishedAt}</span>
        : null;

    // Nothing per-column to drill into (a pull that has not reached the per-column stage, or a table with
    // no GL columns), so the word stands alone rather than offering an empty popover.
    if (!columnIds.length) {
        return <span><strong>GL Pull:</strong> {indicator} {timestamp}</span>;
    }

    return (
        <span>
            <strong>GL Pull:</strong>{' '}
            <Popover
                header="Column pull status"
                position="bottom"
                size="large"
                triggerType="custom"
                dismissButton={false}
                content={(
                    <table className="segmentPullStatusTable">
                        <tbody>
                            {columnIds.map((columnId) => {
                                const columnStatus = configStatuses[columnId];
                                const presentation = PRESENTATION[columnStatus];
                                const error = errors[columnId];
                                return (
                                    <tr key={columnId}>
                                        <td className="pullStatusColumnName">{columnLabel(columnId, columns)}</td>
                                        <td>
                                            {presentation
                                                ? (
                                                    <StatusIndicator type={presentation.type}>
                                                        {presentation.label}
                                                    </StatusIndicator>
                                                )
                                                : <StatusIndicator type="info">{String(columnStatus)}</StatusIndicator>}
                                        </td>
                                        {/* The reason a column failed is the actionable part, so it is shown
                                            inline rather than left in a log. */}
                                        <td className="pullStatusError">{error ?? ''}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            >
                {/* Wrapped so the indicator itself is the hover target and reads as interactive. */}
                <span className="segmentPullStatusTrigger">{indicator}</span>
            </Popover>
            {/* Outside the popover trigger: the finish time is plain information, not something to hover. */}
            {timestamp && <>{' '}{timestamp}</>}
        </span>
    );
}
